'use client'

import Image from 'next/image'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from 'framer-motion'

export type TickerProject = {
  name: string
  href: string
  image: string
  tags: string[]
}

// All projects from the user's screenshots folder.
// Filenames are matched exactly to the assets in
// `/public/images/Project Screenshots/`.
const projects: TickerProject[] = [
  { name: 'Century Tech',          href: 'https://www.centurytech.com/',           image: '/images/Project Screenshots/century tech.webp',  tags: ['Development'] },
  { name: 'Conductor Semi',        href: 'https://conductorsemi.com/',             image: '/images/Project Screenshots/Conductor Semi.webp', tags: ['Development'] },
  { name: 'Coco Kosmetik',         href: 'https://www.cocokosmetik.ch/',           image: '/images/Project Screenshots/coco Kosmetic.webp',  tags: ['Development'] },
  { name: 'Iron',                  href: 'https://iron.xyz/',                      image: '/images/Project Screenshots/IRON.webp',           tags: ['Development'] },
  { name: 'Louis Limited',         href: 'https://louislimitedmentorship.com/',    image: '/images/Project Screenshots/Louis LImited.webp',  tags: ['Development'] },
  { name: 'Nexus',                 href: 'https://nexusapp.io/',                   image: '/images/Project Screenshots/Nexus.webp',          tags: ['Development'] },
  { name: 'Notre',                 href: 'https://www.notre.design/',              image: '/images/Project Screenshots/Notre.webp',          tags: ['Development'] },
  { name: 'NRI Automobile',        href: 'https://www.nri-automobile.ch/',         image: '/images/Project Screenshots/NRI Automobile.webp', tags: ['Development'] },
  { name: 'Perpetual',             href: 'https://soft-group-116031.framer.app/',  image: '/images/Project Screenshots/Perpetual.webp',      tags: ['Development'] },
  { name: 'Printr',                href: 'https://www.printr.money/',              image: '/images/Project Screenshots/Printr.webp',         tags: ['Development'] },
  { name: 'Prop Edge',             href: 'https://www.propsedge.io/',              image: '/images/Project Screenshots/Prop Edge.webp',      tags: ['Development'] },
  { name: 'Visaro',                href: 'https://www.visaro.ch/',                 image: '/images/Project Screenshots/Visaro.webp',         tags: ['Development'] },
  { name: 'Atopia',                href: 'https://atopia.space/',                  image: '/images/Project Screenshots/Atopia.webp',                  tags: ['Development'] },
  { name: 'Burison Real Estate',   href: 'https://realestatereimagined.com.au/',   image: '/images/Project Screenshots/Burison Realestate Agent.webp', tags: ['Development'] },
  { name: 'Medsolve',              href: 'https://www.medsolve.de/',               image: '/images/Project Screenshots/Medsolve.webp',                tags: ['Development'] },
  { name: 'Netlogic',              href: 'https://netlogicsecure.com/',            image: '/images/Project Screenshots/Netlogic.webp',                tags: ['Development'] },
  { name: 'Starlight',             href: 'https://starlightfinal.framer.website/', image: '/images/Project Screenshots/Starlight.webp',               tags: ['Development'] },
  { name: 'Tables and Chairs',     href: 'https://www.tableandchairs.nl/',         image: '/images/Project Screenshots/Tables and Chairs.webp',      tags: ['Development'] },
]

// Reusable inner card surface — image + hover overlay (md+) — used by
// both the ticker rows and the grid view. The outer wrapper controls
// width / spacing in each layout context.
function CardSurface({ project }: { project: TickerProject }) {
  const cardRef = useRef<HTMLAnchorElement>(null)
  const [hovered, setHovered] = useState(false)

  // Mouse position tracked relative to the card. Spring-smoothed so the
  // follower trails the cursor with a soft ease — feels custom rather
  // than the default OS pointer.
  const mx = useMotionValue(0)
  const my = useMotionValue(0)
  const x = useSpring(mx, { stiffness: 300, damping: 30, mass: 0.6 })
  const y = useSpring(my, { stiffness: 300, damping: 30, mass: 0.6 })

  // Center the follower on the cursor.
  const tx = useTransform(x, (v) => `calc(${v}px - 50%)`)
  const ty = useTransform(y, (v) => `calc(${v}px - 50%)`)

  function handleMove(e: React.MouseEvent<HTMLAnchorElement>) {
    const rect = cardRef.current?.getBoundingClientRect()
    if (!rect) return
    mx.set(e.clientX - rect.left)
    my.set(e.clientY - rect.top)
  }

  function handleEnter(e: React.MouseEvent<HTMLAnchorElement>) {
    const rect = cardRef.current?.getBoundingClientRect()
    if (rect) {
      const px = e.clientX - rect.left
      const py = e.clientY - rect.top
      // Initialise instantly at the cursor's current position so the
      // follower doesn't fly in from a stale point.
      mx.set(px)
      my.set(py)
      x.jump(px)
      y.jump(py)
    }
    setHovered(true)
  }

  return (
    <>
      <a
        ref={cardRef}
        href={project.href}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={handleEnter}
        onMouseLeave={() => setHovered(false)}
        onMouseMove={handleMove}
        className="after:hidden group relative block aspect-[2000/1214] rounded-2xl overflow-hidden bg-white/5 border-[3px] border-white cursor-none"
      >
        <Image
          src={project.image}
          alt={project.name}
          fill
          sizes="(max-width: 768px) 320px, (max-width: 1024px) 550px, 650px"
          className="object-cover"
          draggable={false}
        />

        {/* Desktop hover overlay (md+ only) — smooth darkened blur,
            with name (bottom-left) and tags (bottom-right). The CTA is
            now replaced by a mouse-following pill rendered below. */}
        <div
          className="hidden md:flex absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-out backdrop-blur-md bg-black/55 flex-col justify-end p-6 md:p-8"
        >
          <div className="relative flex items-end justify-between gap-6">
            <h3 className="font-display font-medium text-white text-xl md:text-2xl leading-tight">
              {project.name}
            </h3>
            <div className="flex flex-wrap gap-2 justify-end">
              {project.tags.map((t) => (
                <span
                  key={t}
                  className="px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider text-white/90 border border-white/30 bg-white/10"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Custom cursor follower (md+ only). Pill-shaped glass chip
            that trails the cursor with a soft spring. Looks button-ish
            but reads as a cursor, not a click target. */}
        <motion.div
          aria-hidden="true"
          className="hidden md:block pointer-events-none absolute top-0 left-0 z-10"
          style={{ x: tx, y: ty }}
          initial={false}
          animate={{
            opacity: hovered ? 1 : 0,
            scale: hovered ? 1 : 0.7,
          }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="relative inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/95 backdrop-blur-sm text-charcoal-900 font-display font-medium uppercase tracking-wider text-[11px] shadow-[0_8px_28px_-8px_rgba(0,0,0,0.45)] ring-1 ring-white/60">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inset-0 rounded-full bg-brand-500 animate-ping opacity-70" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand-500" />
            </span>
            View Live
            <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 17L17 7" />
              <path d="M8 7h9v9" />
            </svg>
          </div>
        </motion.div>
      </a>

      {/* Mobile-only meta below the card */}
      <div className="md:hidden mt-3 flex items-center justify-between gap-4">
        <p className="font-display font-medium text-charcoal-900 text-base leading-tight">
          {project.name}
        </p>
        <a
          href={project.href}
          target="_blank"
          rel="noopener noreferrer"
          className="after:hidden inline-flex items-center px-4 py-2 rounded-[8px] bg-charcoal-900 text-white font-display font-medium uppercase tracking-wider text-xs"
        >
          View Live
        </a>
      </div>
    </>
  )
}

// Ticker card — fixed widths (320 mobile / 550 tablet / 650 desktop)
function ProjectCardItem({ project }: { project: TickerProject }) {
  return (
    <div className="flex-none w-[320px] md:w-[550px] lg:w-[650px] mr-5">
      <CardSurface project={project} />
    </div>
  )
}

// Grid card — fluid width inside CSS grid
function ProjectCardGridItem({ project }: { project: TickerProject }) {
  return (
    <div className="w-full">
      <CardSurface project={project} />
    </div>
  )
}

function TickerRow({
  items,
  direction,
  speedSec,
}: {
  items: TickerProject[]
  direction: 'left' | 'right'
  speedSec: number
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [paused, setPaused] = useState(false)

  // We render the items twice and animate by -50% (left) or from -50% (right)
  // so the loop is seamless regardless of total width.
  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    el.style.animationPlayState = paused ? 'paused' : 'running'
  }, [paused])

  return (
    <div
      className="overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        ref={trackRef}
        className="flex w-max will-change-transform"
        style={{
          animation: `${direction === 'left' ? 'project-ticker-left' : 'project-ticker-right'} ${speedSec}s linear infinite`,
        }}
      >
        {items.map((p, i) => (
          <ProjectCardItem key={`a-${p.name}-${i}`} project={p} />
        ))}
        {items.map((p, i) => (
          <ProjectCardItem key={`b-${p.name}-${i}`} project={p} />
        ))}
      </div>
    </div>
  )
}

// View-toggle icons. Two stacked horizontal rectangles = ticker view.
// Standard 2x2 grid = grid view.
function TickerIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <rect x="3" y="5" width="18" height="6" rx="1.5" />
      <rect x="3" y="13" width="18" height="6" rx="1.5" />
    </svg>
  )
}

function GridIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </svg>
  )
}

export default function ProjectTicker({ heading }: { heading?: ReactNode }) {
  const [view, setView] = useState<'ticker' | 'grid'>('ticker')

  const half = Math.ceil(projects.length / 2)
  const top = projects.slice(0, half)
  const bottom = projects.slice(half)

  const baseBtn =
    'after:hidden inline-flex items-center justify-center w-11 h-11 rounded-[10px] border transition-colors duration-300'
  const activeBtn = 'bg-brand-500 text-white border-brand-500'
  const idleBtn = 'bg-transparent text-brand-500 border-charcoal-300 hover:border-brand-500'

  return (
    <div>
      {/* Heading row + view toggles. Heading content is provided by
          the parent so brand typography (Pretitle / AnimatedH2) is
          consistent with the rest of the page. */}
      <div className="px-6 md:px-16 mb-12 md:mb-16">
        <div className="max-w-[1600px] mx-auto flex items-end justify-between gap-8">
          <div className="min-w-0">{heading}</div>
          <div className="hidden md:flex items-center gap-2 shrink-0" role="tablist" aria-label="Project view">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'ticker'}
              aria-label="Ticker view"
              onClick={() => setView('ticker')}
              className={`${baseBtn} ${view === 'ticker' ? activeBtn : idleBtn}`}
            >
              <TickerIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'grid'}
              aria-label="Grid view"
              onClick={() => setView('grid')}
              className={`${baseBtn} ${view === 'grid' ? activeBtn : idleBtn}`}
            >
              <GridIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Animated view swap. AnimatePresence cross-fades between
          ticker (full-bleed) and grid (padded container). */}
      <AnimatePresence mode="wait" initial={false}>
        {view === 'ticker' ? (
          <motion.div
            key="ticker"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col gap-5"
          >
            <TickerRow items={top} direction="left" speedSec={60} />
            <TickerRow items={bottom} direction="right" speedSec={60} />
          </motion.div>
        ) : (
          <motion.div
            key="grid"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="px-6 md:px-16"
          >
            <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {projects.map((p) => (
                <ProjectCardGridItem key={p.name} project={p} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
